import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getLocalizedText } from '../cms/localization';
import { productPath } from '../utils/slug';
import type { SupportedLang } from '../cms/localization';
import type { LocalizedText } from '../../shared/types';

interface Hotspot {
  cx: number;
  cy: number;
  r: number;
}

interface PainPointItem {
  id: string;
  iconSvg: string;
  label: LocalizedText;
  advice: LocalizedText;
  productSlug?: string;
  hotspots: Hotspot[];
}

interface PainPointFinderProps {
  content: Record<string, unknown>;
}

const emptyLocalized = (): LocalizedText => ({ 'zh-HK': '', 'zh-CN': '', en: '' });

const defaultItems: PainPointItem[] = [
  {
    id: 'neck',
    iconSvg: '',
    label: emptyLocalized(),
    advice: emptyLocalized(),
    hotspots: [{ cx: 100, cy: 60, r: 12 }],
  },
  {
    id: 'back',
    iconSvg: '',
    label: emptyLocalized(),
    advice: emptyLocalized(),
    hotspots: [{ cx: 100, cy: 103, r: 12 }],
  },
  {
    id: 'cold',
    iconSvg: '',
    label: emptyLocalized(),
    advice: emptyLocalized(),
    hotspots: [
      { cx: 46, cy: 144, r: 11 },
      { cx: 154, cy: 144, r: 11 },
    ],
  },
];

const PainPointFinder: React.FC<PainPointFinderProps> = ({ content }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;

  const eyebrow = getLocalizedText(content.eyebrow, lang, '經絡與痛點檢索');
  const titlePrefix = getLocalizedText(content.titlePrefix, lang, '點擊您的');
  const titleAccent = getLocalizedText(content.titleAccent, lang, '不適部位');
  const subtitle = getLocalizedText(content.subtitle, lang, '獲取專屬養生建議，對症溫養');
  const suggest = getLocalizedText(content.suggest, lang, '建議使用');
  const ctaText = getLocalizedText(content.ctaText, lang, '查看適用產品 →');

  const items = useMemo(() => {
    const raw = (content.items as PainPointItem[] | undefined) || defaultItems;
    return raw.length > 0 ? raw : defaultItems;
  }, [content.items]);

  const [selectedId, setSelectedId] = useState<string>(items[0]?.id || '');

  const selectedItem = useMemo(
    () => items.find((i) => i.id === selectedId) || items[0],
    [items, selectedId]
  );

  const handleSelect = (id: string) => {
    if (id) setSelectedId(id);
  };

  return (
    <section
      id="pain-points"
      className="relative py-12 md:py-16 overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at 12% 10%, rgba(var(--brand-gold-rgb),0.07), transparent 40%), radial-gradient(circle at 88% 90%, rgba(var(--brand-crimson-rgb),0.06), transparent 45%), var(--brand-paper)',
      }}
    >
      <style>{`
        @keyframes ppf-breathe {
          0% { opacity: 0.7; transform: scale(0.85); }
          70% { opacity: 0; transform: scale(1.7); }
          100% { opacity: 0; transform: scale(1.7); }
        }
        .ppf-ring {
          transform-origin: center;
          animation: ppf-breathe 2.4s ease-in-out infinite;
        }
        .ppf-hotspot:not(.active) .ppf-ring {
          animation: none;
          opacity: 0;
        }
        .ppf-card-arrow {
          position: absolute;
          top: -8px;
          left: 50%;
          width: 14px;
          height: 14px;
          background: #FFFDF8;
          border-left: 1.5px solid var(--brand-crimson);
          border-top: 1.5px solid var(--brand-crimson);
          transform: translateX(-50%) rotate(45deg);
          border-radius: 2px 0 0 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .ppf-ring { animation: none !important; }
        }
      `}</style>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          {eyebrow && (
            <span className="eyebrow mb-4">
              <span className="w-6 h-[1px] bg-current opacity-60" />
              {eyebrow}
              <span className="w-6 h-[1px] bg-current opacity-60" />
            </span>
          )}
          <h2 className="text-3xl md:text-4xl font-serif text-brand-ink leading-tight mt-2">
            {titlePrefix}
            <span className="text-brand-cinnabar">{titleAccent}</span>
          </h2>

          {/* Diamond divider */}
          <div className="mt-5 flex items-center justify-center gap-2.5">
            <span className="w-16 h-[1px] bg-gradient-to-r from-transparent to-brand-gold" />
            <span className="w-1.5 h-1.5 bg-brand-gold rotate-45" />
            <span className="w-16 h-[1px] bg-gradient-to-l from-transparent to-brand-gold" />
          </div>

          <p className="text-center text-brand-ink/60 font-sans text-sm mt-4 mb-10">
            {subtitle}
          </p>
        </div>

        {/* Body map */}
        <div className="flex justify-center mb-2">
          <svg className="w-[180px] h-auto overflow-visible" viewBox="0 0 200 220" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            {/* Head */}
            <circle className="fill-brand-stone stroke-[#EAD9AE]" strokeWidth="1.5" cx="100" cy="34" r="18" />
            {/* Neck */}
            <rect className="fill-brand-stone stroke-[#EAD9AE]" strokeWidth="1.5" x="94" y="50" width="12" height="12" rx="3" />
            {/* Torso */}
            <path
              className="fill-brand-stone stroke-[#EAD9AE]"
              strokeWidth="1.5"
              d="M62,70 C56,88 59,110 66,126 L134,126 C141,110 144,88 138,70 C126,60 74,60 62,70 Z"
            />
            {/* Arms */}
            <path className="fill-none stroke-[#EAD9AE]" strokeWidth="1.5" d="M65,76 C50,92 44,116 46,140" />
            <path className="fill-none stroke-[#EAD9AE]" strokeWidth="1.5" d="M135,76 C150,92 156,116 154,140" />
            <circle className="fill-brand-stone stroke-[#EAD9AE]" strokeWidth="1.5" cx="46" cy="144" r="7" />
            <circle className="fill-brand-stone stroke-[#EAD9AE]" strokeWidth="1.5" cx="154" cy="144" r="7" />
            {/* Seated base */}
            <path
              className="fill-brand-stone stroke-[#EAD9AE]"
              strokeWidth="1.5"
              d="M38,168 C38,138 66,126 100,126 C134,126 162,138 162,168 C162,176 38,176 38,168 Z"
            />

            {/* Hotspots */}
            {items.map((item) =>
              (item.hotspots || []).map((h, idx) => {
                const active = selectedId === item.id;
                const label = getLocalizedText(item.label, lang);
                return (
                  <g
                    key={`${item.id}-${idx}`}
                    className={`ppf-hotspot cursor-pointer transition-colors ${active ? 'active' : ''}`}
                    tabIndex={idx === 0 ? 0 : -1}
                    role="button"
                    aria-label={label}
                    aria-hidden={idx !== 0}
                    onClick={() => handleSelect(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(item.id);
                      }
                    }}
                  >
                    <circle className="ppf-ring fill-none stroke-brand-cinnabar" strokeWidth="1.5" cx={h.cx} cy={h.cy} r={h.r} />
                    <circle
                      className={`fill-brand-gold transition-all duration-300 ${active ? 'fill-brand-cinnabar' : ''}`}
                      cx={h.cx}
                      cy={h.cy}
                      r={active ? 6 : 5}
                    />
                  </g>
                );
              })
            )}
          </svg>
        </div>

        {/* Cards row */}
        <div className="flex gap-3.5 justify-center flex-wrap mt-7">
          {items.map((item) => {
            const label = getLocalizedText(item.label, lang);
            const active = selectedId === item.id;
            return (
              <motion.button
                key={item.id}
                type="button"
                onClick={() => handleSelect(item.id)}
                whileHover={{ y: -3 }}
                className={`relative flex-[0_1_200px] bg-white border rounded-xl p-4 text-center transition-all duration-300 ${
                  active
                    ? 'border-brand-cinnabar shadow-lg bg-gradient-to-b from-white to-brand-stone'
                    : 'border-brand-gold/30 hover:border-brand-gold/60 hover:shadow-md'
                }`}
              >
                {active && <span className="ppf-card-arrow" aria-hidden="true" />}
                <span
                  className={`absolute top-2 right-2 w-4 h-4 rounded-full bg-brand-cinnabar text-white text-[10px] flex items-center justify-center transition-all duration-300 ${
                    active ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                  }`}
                >
                  ✓
                </span>
                <span
                  className={`w-10 h-10 mx-auto mb-2.5 rounded-full flex items-center justify-center transition-all duration-300 ${
                    active ? 'bg-brand-gold/30 scale-105' : 'bg-brand-stone'
                  } text-brand-cinnabar`}
                  dangerouslySetInnerHTML={{ __html: item.iconSvg || '<svg viewBox="0 0 24 24" />' }}
                />
                <span className="font-serif text-[15.5px] font-medium text-brand-ink">{label}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Recommendation panel */}
        <div className="mt-5 mx-auto max-w-xl bg-white border border-brand-gold/30 rounded-xl p-6 text-center border-t-[3px] border-t-brand-cinnabar min-h-[118px] flex flex-col items-center justify-center">
          <AnimatePresence mode="wait">
            {selectedItem && (
              <motion.div
                key={selectedItem.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
              >
                <p
                  className="text-sm leading-7 text-brand-ink mb-3.5"
                  dangerouslySetInnerHTML={{
                    __html: `<strong class="text-brand-cinnabar">${suggest}：</strong>${getLocalizedText(
                      selectedItem.advice,
                      lang
                    )}`,
                  }}
                />
                <Link
                  to={productPath(selectedItem.productSlug)}
                  className="inline-block font-sans font-bold text-sm text-brand-cinnabar border-b-[1.5px] border-brand-cinnabar pb-0.5 hover:text-brand-crimson hover:border-brand-crimson transition-colors"
                >
                  {ctaText}
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default PainPointFinder;
